import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface ARInvoiceDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave?: (updatedData: any, changes?: any[]) => void
}

const ARInvoiceDialog: React.FC<ARInvoiceDialogProps> = ({ data, shipmentId, documentId, onSave }) => {
  // Define sections for AR Invoice
  const sections: DocumentSection[] = [
    {
      id: 'invoiceInfo',
      title: 'Invoice Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Invoice #', path: 'invoiceNumber' },
        { type: 'field', label: 'Sales Order #', path: 'salesOrderNumber' },
        { type: 'field', label: 'Customer PO #', path: 'customerPurchaseOrderNumber' },
        { type: 'field', label: 'Reference #', path: 'referenceNumber' },
        { type: 'field', label: 'Invoice Date', path: 'invoiceDate' },
        { type: 'field', label: 'Due Date', path: 'dueDate' },
        { type: 'field', label: 'Currency', path: 'currency' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Seller',
          path: 'seller',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Contact', path: 'contact' },
          ],
        },
        {
          type: 'object',
          label: 'Customer',
          path: 'customer',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Contact', path: 'contact' },
            { label: 'Customer Number', path: 'customerNumber' },
            { label: 'Payment Terms', path: 'paymentTerms' },
          ],
        },
      ],
    },
    {
      id: 'bankDetails',
      title: 'Bank Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Bank Details',
          path: 'seller.bankDetails',
          fields: [
            { label: 'Bank Name', path: 'bankName' },
            { label: 'Account Number', path: 'accountNumber' },
            { label: 'Routing Number', path: 'routingNumber' },
            { label: 'SWIFT Code', path: 'swiftCode' },
            { label: 'IBAN', path: 'iban' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'lineItems',
      title: 'Line Items',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Line Items',
          path: 'lineItems',
          columns: [
            { label: 'Item Number', path: 'itemNumber' },
            { label: 'Description', path: 'description' },
            { label: 'Quantity', path: 'quantity' },
            { label: 'Unit Price', path: 'unitPrice' },
            { label: 'Unit of Measure', path: 'unitOfMeasure' },
            { label: 'Amount', path: 'amount' },
            { label: 'Tax Rate', path: 'taxRate' },
            { label: 'Tax Amount', path: 'taxAmount' },
            { label: 'Discount %', path: 'discountPercentage' },
            { label: 'Discount Amount', path: 'discountAmount' },
            { label: 'GL Account', path: 'glAccount' },
            { label: 'Sales Person', path: 'salesPerson' },
          ],
        },
      ],
    },
    {
      id: 'totals',
      title: 'Totals',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Subtotal', path: 'subtotal' },
        { type: 'field', label: 'Tax Total', path: 'taxTotal' },
        { type: 'field', label: 'Discount Total', path: 'discountTotal' },
        { type: 'field', label: 'Shipping Amount', path: 'shippingAmount' },
        { type: 'field', label: 'Total Amount', path: 'totalAmount' },
        { type: 'field', label: 'Amount Due', path: 'amountDue' },
      ],
    },
    {
      id: 'otherCharges',
      title: 'Other Charges',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Other Charges',
          path: 'otherCharges',
          columns: [
            { label: 'Description', path: 'description' },
            { label: 'Amount', path: 'amount' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'paymentInfo',
      title: 'Payment Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Payment Status', path: 'paymentStatus' },
        { type: 'field', label: 'Payment Method', path: 'paymentMethod' },
        { type: 'field', label: 'Payment Due Date', path: 'paymentDueDate' },
      ],
    },
    {
      id: 'earlyPaymentDiscount',
      title: 'Early Payment Discount',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Early Payment Discount',
          path: 'earlyPaymentDiscount',
          fields: [
            { label: 'Percentage', path: 'percentage' },
            { label: 'Days Valid', path: 'daysValid' },
            { label: 'Amount', path: 'amount' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'payments',
      title: 'Payment History',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Payments',
          path: 'payments',
          columns: [
            { label: 'Payment Date', path: 'paymentDate' },
            { label: 'Amount', path: 'amount' },
            { label: 'Method', path: 'method' },
            { label: 'Reference Number', path: 'referenceNumber' },
            { label: 'Notes', path: 'notes' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'shipmentDetails',
      title: 'Shipment Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Shipment Details',
          path: 'shipmentDetails',
          fields: [
            { label: 'Ship Date', path: 'shipDate' },
            { label: 'Delivery Date', path: 'deliveryDate' },
            { label: 'Ship Method', path: 'shipMethod' },
            { label: 'Tracking Number', path: 'trackingNumber' },
            { label: 'Incoterms', path: 'incoterms' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'shipToAddress',
      title: 'Ship To Address',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Ship To Address',
          path: 'shipmentDetails.shipToAddress',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Street', path: 'street' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'Postal Code', path: 'postalCode' },
            { label: 'Country', path: 'country' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'billToAddress',
      title: 'Bill To Address',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Bill To Address',
          path: 'shipmentDetails.billToAddress',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Street', path: 'street' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'Postal Code', path: 'postalCode' },
            { label: 'Country', path: 'country' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'taxDetails',
      title: 'Tax Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Tax Details',
          path: 'taxDetails',
          fields: [
            { label: 'Tax Registration Number', path: 'taxRegistrationNumber' },
            { label: 'Tax Type', path: 'taxType' },
            { label: 'Tax Exemption Number', path: 'taxExemptionNumber' },
            { label: 'Tax Exemption Reason', path: 'taxExemptionReason' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'recurringInvoice',
      title: 'Recurring Invoice',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Recurring Invoice',
          path: 'recurringInvoice',
          fields: [
            { label: 'Is Recurring', path: 'isRecurring' },
            { label: 'Frequency', path: 'frequency' },
            { label: 'Next Invoice Date', path: 'nextInvoiceDate' },
            { label: 'End Date', path: 'endDate' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'attachments',
      title: 'Attachments',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Attachments',
          path: 'attachments',
          columns: [
            { label: 'Name', path: 'name' },
            { label: 'Type', path: 'type' },
            { label: 'Reference', path: 'reference' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Notes', path: 'notes', fullWidth: true },
        { type: 'field', label: 'Terms', path: 'terms', fullWidth: true },
        { type: 'field', label: 'Status', path: 'status' },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="AR Invoice"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default ARInvoiceDialog
